import {
  EXPERIMENTS_ENABLED_KEY,
  MAX_PENDING_ANALYTICS_CALLS,
  RC_FETCH_TIMEOUT_MS,
  RC_MIN_FETCH_INTERVAL_MS,
  RC_READY_TIMEOUT_MS,
  REMOTE_CONFIG_DEFAULTS,
} from './defaults.js'

let analyticsInstance = null
let analyticsApi = null
let remoteConfigInstance = null
let remoteConfigApi = null

let initStarted = false
let readyPromise = null
let resolveReady = null

let pendingAnalyticsCalls = []
let isAnalyticsSettled = false

function markReady() {
  if (resolveReady) {
    const resolve = resolveReady
    resolveReady = null
    resolve()
  }
}

async function bootstrap({ config }) {
  const [{ initializeApp }, analyticsModule, remoteConfigModule] = await Promise.all([
    import('firebase/app'),
    import('firebase/analytics'),
    import('firebase/remote-config'),
  ])

  const app = initializeApp(config)

  if (await analyticsModule.isSupported()) {
    analyticsApi = analyticsModule
    analyticsInstance = analyticsModule.initializeAnalytics(app, { config: { send_page_view: false } })
    flushPendingAnalytics()
  } else {
    dropPendingAnalytics()
  }

  remoteConfigApi = remoteConfigModule
  const remoteConfig = remoteConfigModule.getRemoteConfig(app)
  remoteConfig.defaultConfig = { ...REMOTE_CONFIG_DEFAULTS }
  remoteConfig.settings.minimumFetchIntervalMillis = RC_MIN_FETCH_INTERVAL_MS
  remoteConfig.settings.fetchTimeoutMillis = RC_FETCH_TIMEOUT_MS
  remoteConfigInstance = remoteConfig

  await remoteConfigModule.ensureInitialized(remoteConfig)
  await remoteConfigModule.activate(remoteConfig)

  if (isRemoteValue(EXPERIMENTS_ENABLED_KEY)) {
    markReady()
  }

  await remoteConfigModule.fetchAndActivate(remoteConfig)
  markReady()
}

export function initFirebase({ config }) {
  if (initStarted || !config?.apiKey) {
    return
  }
  initStarted = true

  readyPromise = new Promise((resolve) => {
    resolveReady = resolve
  })
  setTimeout(markReady, RC_READY_TIMEOUT_MS)

  bootstrap({ config }).catch(() => {
    dropPendingAnalytics()
    markReady()
  })
}

export function whenRemoteConfigReady() {
  if (!initStarted) {
    return Promise.resolve()
  }
  return readyPromise
}

function readRemote(reader, key) {
  if (remoteConfigInstance && remoteConfigApi) {
    try {
      return reader(remoteConfigInstance, key)
    } catch {
      return REMOTE_CONFIG_DEFAULTS[key]
    }
  }

  return REMOTE_CONFIG_DEFAULTS[key]
}

export function getRemoteString(key) {
  return readRemote((rc, k) => remoteConfigApi.getString(rc, k), key) ?? ''
}

export function getRemoteBoolean(key) {
  return readRemote((rc, k) => remoteConfigApi.getBoolean(rc, k), key) ?? false
}

export function getRemoteNumber(key) {
  return readRemote((rc, k) => remoteConfigApi.getNumber(rc, k), key) ?? 0
}

export function isRemoteValue(key) {
  if (!remoteConfigInstance || !remoteConfigApi) {
    return false
  }

  try {
    return remoteConfigApi.getValue(remoteConfigInstance, key).getSource() === 'remote'
  } catch {
    return false
  }
}

function invokeAnalyticsCall(call) {
  try {
    call(analyticsApi, analyticsInstance)
    return true
  } catch {
    return false
  }
}

function callAnalytics(call) {
  if (analyticsInstance && analyticsApi) {
    invokeAnalyticsCall(call)
    return
  }

  if (isAnalyticsSettled || pendingAnalyticsCalls.length >= MAX_PENDING_ANALYTICS_CALLS) {
    return
  }

  pendingAnalyticsCalls.push(call)
}

function flushPendingAnalytics() {
  const pending = pendingAnalyticsCalls
  pendingAnalyticsCalls = []
  isAnalyticsSettled = true

  pending.forEach(invokeAnalyticsCall)
}

function dropPendingAnalytics() {
  pendingAnalyticsCalls = []
  isAnalyticsSettled = true
}

export function logAnalyticsEvent(name, params) {
  callAnalytics((api, instance) => api.logEvent(instance, name, params))
}

export function setAnalyticsUserId(id) {
  if (id === null || id === undefined) {
    return
  }
  callAnalytics((api, instance) => api.setUserId(instance, String(id)))
}

export function setAnalyticsUserProperties(props) {
  callAnalytics((api, instance) => api.setUserProperties(instance, props))
}

export { CONTROL_VARIANT, EXPERIMENT_KEY_PREFIX, EXPERIMENTS_ENABLED_KEY, REMOTE_CONFIG_DEFAULTS } from './defaults.js'
